import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useCompareThePair } from '../hooks/useCompareThePair';
import { CTPTopBar } from '../components/compare-the-pair/CTPTopBar';
import { CTPProgressTracker } from '../components/compare-the-pair/CTPProgressTracker';
import { CTPSlide } from '../components/compare-the-pair/CTPSlide';
import { CTPResultsBanner } from '../components/compare-the-pair/CTPResultsBanner';
import { CTPVoteSlide } from '../components/compare-the-pair/CTPVoteSlide';
import { Footer } from '../components/layout/Footer';

export const CompareThePairPage: React.FC = () => {
  const { state, currentQuestion, answer, nextQuestion, prevQuestion, startGame, savedVote, saveVote, completeVote, reviewQuestions } = useCompareThePair();
  const { data } = state;
  const totalQuestions = data?.questions.length || 10;

  // Keyboard support
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (state.status === 'ready') {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          startGame();
        }
      } else if (state.status === 'playing') {
        if (e.key === 'ArrowLeft' || e.key === '1') {
          e.preventDefault();
          answer('A');
        } else if (e.key === 'ArrowRight' || e.key === '2') {
          e.preventDefault();
          answer('B');
        }
      } else if (state.status === 'revealed') {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          nextQuestion();
        }
      } else if (state.status === 'voting' && !savedVote) {
        if (e.key === 'ArrowLeft' || e.key === '1') {
          e.preventDefault();
          saveVote('A');
        } else if (e.key === 'ArrowRight' || e.key === '2') {
          e.preventDefault();
          saveVote('B');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [state.status, answer, nextQuestion, startGame, savedVote, saveVote, completeVote]);

  // Determine if displayed question's answer was correct
  const displayedAnswer = state.answeredQuestions[state.currentIndex];
  const wasCorrect = displayedAnswer?.correct ?? false;

  return (
    <>
      <Helmet>
        <title>Wallstreetle | Compare the Pair</title>
        <meta
          name="description"
          content="Compare two companies from the same industry across 10 financial metrics. A new pair every day!"
        />
      </Helmet>

      <div
        className={`flex flex-col bg-background ${state.status === 'complete' ? 'min-h-[100dvh]' : 'h-[100dvh] overflow-hidden'}`}
      >
        <CTPTopBar />

        {/* Content area below fixed top bar */}
        <div
          className="flex-1 flex flex-col min-h-0 px-3 sm:px-4 pb-2"
          style={{ paddingTop: 'calc(3rem + env(safe-area-inset-top))' }}
        >
          {/* Loading / No pair available */}
          {(state.status === 'idle' || state.status === 'loading') && (
            <div className="flex-1 flex items-center justify-start pt-12">
              {state.error ? (
                <div />
              ) : (
                <div
                  className="text-sm font-medium animate-pulse text-muted-foreground"
                >
                  Loading Today's Pair...
                </div>
              )}
            </div>
          )}

          {/* Intro / Ready screen */}
          {state.status === 'ready' && data && (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 max-w-lg mx-auto w-full overflow-y-auto relative">
              <p
                className="opacity-0 animate-fade-up text-center font-serif italic text-base sm:text-lg leading-relaxed px-4 text-foreground -mt-8"
              >
                "If you're just looking at the price, you're not investing."
                <span className="block mt-1 not-italic text-sm text-muted-foreground">
                  — Warren Buffett
                </span>
              </p>

              <span
                className="opacity-0 animate-fade-up-delay inline-block text-xs font-medium px-3 py-1 rounded-full text-muted-foreground bg-card"
              >
                {data.industry}
              </span>

              <div className="opacity-0 animate-fade-up-delay-2 flex items-stretch gap-3 w-full px-2">
                {/* Company A */}
                <div
                  className="flex-1 rounded-xl p-3 flex flex-col items-center text-center gap-1 bg-card"
                >
                  {data.logoUrlA ? (
                    <img src={data.logoUrlA} alt={data.nameA} className="w-12 h-12 rounded-lg object-contain" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center text-lg font-bold text-foreground bg-muted">
                      {data.symbolA[0]}
                    </div>
                  )}
                  <div className="font-semibold text-sm text-foreground">{data.nameA}</div>
                  <div className="text-sm font-mono text-muted-foreground">{data.symbolA}</div>
                  {data.descriptionA && (
                    <p className="text-sm leading-snug text-muted-foreground">
                      {data.descriptionA}
                    </p>
                  )}
                </div>

                {/* VS divider */}
                <span className="font-serif font-bold text-lg shrink-0 self-center text-muted-foreground">VS</span>

                {/* Company B */}
                <div
                  className="flex-1 rounded-xl p-3 flex flex-col items-center text-center gap-1 bg-card"
                >
                  {data.logoUrlB ? (
                    <img src={data.logoUrlB} alt={data.nameB} className="w-12 h-12 rounded-lg object-contain" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center text-lg font-bold text-foreground bg-muted">
                      {data.symbolB[0]}
                    </div>
                  )}
                  <div className="font-semibold text-sm text-foreground">{data.nameB}</div>
                  <div className="text-sm font-mono text-muted-foreground">{data.symbolB}</div>
                  {data.descriptionB && (
                    <p className="text-sm leading-snug text-muted-foreground">
                      {data.descriptionB}
                    </p>
                  )}
                </div>
              </div>

              <button
                onClick={startGame}
                className="opacity-0 animate-fade-up-delay-3 w-full max-w-xs py-3 rounded-xl font-semibold text-base text-white transition-opacity hover:opacity-90 active:scale-[0.98]"
                style={{ backgroundColor: 'var(--ctp-accent)' }}
              >
                Start Comparing
              </button>
            </div>
          )}

          {/* Game in progress */}
          {(state.status === 'playing' || state.status === 'revealed') && data && currentQuestion && (
            <>
              {/* Progress tracker */}
              <div className="mb-2">
                <CTPProgressTracker answers={state.answers} total={totalQuestions} />
              </div>

              {/* Question text — two lines */}
              {(() => {
                const clean = currentQuestion.questionText.replace(/\?$/, '');
                const match = clean.match(/^(Which company has)(?: a)? (\w+) (.+)$/i);
                if (!match) {
                  return (
                    <h2 className="text-center text-base sm:text-lg font-serif mb-2 sm:mb-3 px-2 text-foreground">
                      {currentQuestion.questionText}
                    </h2>
                  );
                }
                const [, prefix, direction, metric] = match;
                return (
                  <h2 className="text-center font-serif mb-2 sm:mb-3 px-2 text-foreground">
                    <span className="block text-base sm:text-lg font-normal text-muted-foreground">
                      {prefix}
                    </span>
                    <span className="block text-xl sm:text-2xl mt-0.5 uppercase tracking-wide">
                      <span className="underline underline-offset-4 decoration-2" style={{ textDecorationColor: 'var(--ctp-accent)' }}>
                        {direction}
                      </span>
                      {' '}
                      <span className="font-bold">
                        {metric}
                      </span>
                      ?
                    </span>
                  </h2>
                );
              })()}

              {/* Slide — fills remaining space */}
              <CTPSlide
                question={currentQuestion}
                nameA={data.nameA}
                nameB={data.nameB}
                symbolA={data.symbolA}
                symbolB={data.symbolB}
                logoUrlA={data.logoUrlA}
                logoUrlB={data.logoUrlB}
                descriptionA={data.descriptionA}
                descriptionB={data.descriptionB}
                onAnswer={answer}
                onNext={nextQuestion}
                onPrev={prevQuestion}
                canGoBack={state.currentIndex > 0}
                isRevealed={state.status === 'revealed'}
                lastAnswer={state.lastAnswer}
                wasCorrect={wasCorrect}
                isLast={state.currentIndex >= totalQuestions - 1}
              />
            </>
          )}

          {/* Vote slide */}
          {state.status === 'voting' && data && (
            <>
              <div className="mb-2">
                <CTPProgressTracker answers={state.answers} total={totalQuestions} />
              </div>
              <CTPVoteSlide
                nameA={data.nameA}
                nameB={data.nameB}
                symbolA={data.symbolA}
                symbolB={data.symbolB}
                logoUrlA={data.logoUrlA}
                logoUrlB={data.logoUrlB}
                gameDate={data.gameDate}
                savedVote={savedVote}
                onVoteSaved={saveVote}
                onComplete={completeVote}
                onPrev={prevQuestion}
                answeredQuestions={state.answeredQuestions}
              />
            </>
          )}

          {/* Results */}
          {state.status === 'complete' && data && (
            <>
              <div className="mb-2">
                <CTPProgressTracker answers={state.answers} total={totalQuestions} />
              </div>
              <CTPResultsBanner
                score={state.score}
                answers={state.answers}
                nameA={data.nameA}
                nameB={data.nameB}
                industry={data.industry}
                gameDate={data.gameDate}
                answeredQuestions={state.answeredQuestions}
                symbolA={data.symbolA}
                symbolB={data.symbolB}
                logoUrlA={data.logoUrlA}
                logoUrlB={data.logoUrlB}
                onReview={reviewQuestions}
              />
            </>
          )}
        </div>

        {/* Footer — only on results, below the fold */}
        {state.status === 'complete' && (
          <Footer />
        )}
      </div>
    </>
  );
};
